import { DefaultPrimaryButton, DefaultSecondaryButton } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { DetailCard } from '../../components/detail-card.component';
import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import ProcessOpenCloseSlots from '../../components/slot-open-displays/process-open-close-slots/process-open-close-slots.component';
import { useBulkRemoveConfirm } from './bulk-remove-confirm.controller';

const ID = 'bulk-remove-confirm';

const BulkRemoveConfirm = () => {
    const viewProps = useBulkRemoveConfirm();

    if (viewProps.phase === 'opening') {
        return (
            <div
                id={ID}
                className='bg-background-default flex size-full flex-col overflow-hidden'>
                <KioskNavigationHeader title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)} />
                <div className='flex flex-1 flex-col overflow-hidden'>
                    <ProcessOpenCloseSlots
                        packages={viewProps.openingPackages}
                        onContinue={viewProps.handleOpeningContinue}
                    />
                </div>
            </div>
        );
    }

    if (viewProps.phase === 'done') {
        return (
            <div
                id={ID}
                className='bg-background-default flex size-full flex-col overflow-hidden'>
                <KioskNavigationHeader title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)} />
                <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-6'>
                    <p className='text-foreground-default text-lg font-semibold'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.remove-done.title'
                            defaultMessage='Remove complete'
                            description='Title shown after bulk remove finishes without SLOT open UI'
                        />
                    </p>
                    <p className='text-foreground-muted text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.remove-done.summary'
                            defaultMessage='{successCount, plural, one {# transaction removed} other {# transactions removed}}{failureCount, plural, =0 {} one { · # failed} other { · # failed}}.'
                            description='Summary after bulk remove completes'
                            values={{ successCount: viewProps.successCount, failureCount: viewProps.failureCount }}
                        />
                    </p>
                </div>
                <KioskNavigationFooter
                    rightButton={
                        <DefaultPrimaryButton
                            text={CommonMessage.BUTTONS.CONTINUE}
                            onClick={viewProps.handleOpeningContinue}
                        />
                    }
                />
            </div>
        );
    }

    return (
        <div
            id={ID}
            className='bg-background-default flex size-full flex-col overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={viewProps.handleBack}
            />
            <div className='flex flex-1 flex-col gap-4 overflow-y-auto p-6'>
                <p className='text-foreground-default text-lg font-semibold'>
                    <FormattedMessage
                        id='kiosk.manage-spot.bulk-move-transactions.remove.confirm.title'
                        defaultMessage='Remove transactions'
                        description='Title on the kiosk bulk remove confirmation screen'
                    />
                </p>
                {viewProps.error && <div className='text-status-danger'>{viewProps.error}</div>}
                <DetailCard title={null}>
                    <DetailCard.Row
                        label={CommonMessage.OBJECTS.TRANSACTION.PLURAL}
                        value={String(viewProps.transactions.length)}
                    />
                </DetailCard>
                <ul className='text-brand-muted flex w-full flex-col gap-2 text-sm font-medium'>
                    {viewProps.transactions.map((tx) => (
                        <li
                            key={tx.id}
                            className='border-border-default bg-background-surface rounded-xl border px-3 py-2'>
                            {tx.tracking_number ?? tx.id}
                        </li>
                    ))}
                </ul>
            </div>
            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={viewProps.handleBack}
                        disabled={viewProps.isSubmitting}
                    />
                }
                rightButton={
                    <DefaultPrimaryButton
                        text={CommonMessage.BUTTONS.CONFIRM}
                        onClick={viewProps.handleConfirm}
                        disabled={!viewProps.canConfirm}
                        isLoading={viewProps.isSubmitting}
                    />
                }
            />
        </div>
    );
};

export default BulkRemoveConfirm;
